<template>
    <div>
        <!-- FILTERS -->
        <div class="row">
            <!-- FILTER: NAME -->
            <div class="col-sm-4">
                <div class="form-group">
                    <label for="filterDrugSafeUserName">{{ $t('name') }}</label>
                    <input type="text" id="filterDrugSafeUserName" class="form-control" v-model="filterName" />
                </div>
            </div>
            <!-- FILTER: ROLE -->
            <div class="col-sm-4">
                <div class="form-group">
                    <label for="filterDrugSafeUserRole">{{ $t('role') }}</label>
                    <select id="filterDrugSafeUserRole" class="form-control" v-model="filterRole">
                        <option value="all" selected>{{ $t('all') }}</option>
                        <option v-for="role in rolesDrugSafe" v-bind:key="role" v-bind:value="role">
                            {{ $t(`role_${role}`) }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <!-- TABLE -->
        <div class="mt-4">
            <b-table striped hover :fields="tableFields" :items="filteredUsers" sort-by="last_name">
                <template #cell(role)="data">
                    {{ $t(`role_${data.item.role}`) }}
                </template>
                <template #cell(status)="data">
                    {{ $t(data.item.status) }}
                </template>
                <template #cell(created_at)="data">
                    {{ getLocalDate(data.item.created_at, $i18n.locale, true, true) }}
                </template>
                <template #cell(login_date)="data">
                    {{ getLocalDate(data.item.login_date, $i18n.locale, true, true) }}
                </template>
                <!-- BUTTON: EDIT USER -->
                <template #cell(button_edit)="data">
                    <button
                        class="btn btn-sm btn-info"
                        v-on:click="
                            $router.push({
                                name: 'drugSafeUser.edit',
                                params: { id: pharmacyId, second_id: data.item.id }
                            })
                        "
                        :disabled="submitClicked"
                    >
                        {{ $t('edit') }}
                    </button>
                </template>
                <!-- BUTTON: DELETE USER -->
                <template #cell(button_delete)="data">
                    <button
                        class="btn btn-sm btn-danger"
                        v-on:click="() => handleDeleteDrugSafeUserClick(data.item.id)"
                        :disabled="submitClicked"
                    >
                        {{ $t('delete') }}
                    </button>
                </template>
            </b-table>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE DRUGSAFE USER? -->
        <b-modal
            id="deleteDrugSafeUserModal"
            centered
            :title="$t('delete_drugsafe_user_are_you_sure_title')"
            hide-footer
        >
            <div class="row">
                <div class="col">
                    <p>{{ $t('delete_drugsafe_user_are_you_sure') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="deleteDrugSafeUser">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleDeleteDrugSafeUserCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { deleteDrugSafeUser, getDrugSafeUsers, getRoles, handleAxiosError } from '@/services/axiosService';
import { getLocalDate } from '@/services/dateService';

export default {
    props: {
        pharmacyId: {
            required: true,
            type: String
        }
    },
    data() {
        return {
            deleteDrugSafeUserId: null,
            drugSafeUsers: [],
            filterName: '',
            filterRole: 'all',
            getLocalDate,
            rolesDrugSafe: [],
            submitClicked: false
        };
    },
    async created() {
        // get drugsafe users and roles
        await this.getDrugSafeUsers();
        await this.getRoles();
        // set component loaded
        this.$emit('loaded');
    },
    computed: {
        ...mapState('account', ['user']),
        filteredUsers() {
            return this.drugSafeUsers.filter(item => {
                return (
                    ((item.first_name !== null &&
                        item.first_name.toLowerCase().indexOf(this.filterName.toLowerCase()) > -1) ||
                        (item.last_name !== null &&
                            item.last_name.toLowerCase().indexOf(this.filterName.toLowerCase()) > -1) ||
                        (this.filterName === '' && item.last_name === null && item.first_name === null)) &&
                    (this.filterRole === 'all' || item.role === this.filterRole)
                );
            });
        },
        tableFields() {
            let tableFields = [
                { key: 'first_name', label: this.$t('first_name'), sortable: true, tdClass: 'align-middle' },
                { key: 'last_name', label: this.$t('last_name'), sortable: true, tdClass: 'align-middle' },
                { key: 'role', label: this.$t('role'), sortable: true, tdClass: 'align-middle' },
                { key: 'created_at', label: this.$t('created_at'), sortable: true, tdClass: 'align-middle' },
                { key: 'login_date', label: this.$t('last_login_date'), sortable: true, tdClass: 'align-middle' }
            ];
            if (this.user.permissions.edit_user) {
                tableFields = [
                    ...tableFields,
                    { key: 'button_edit', sortable: false, label: '', tdClass: 'align-middle' },
                    { key: 'button_delete', sortable: false, label: '', tdClass: 'align-middle' }
                ];
            }
            return tableFields;
        }
    },
    methods: {
        // delete drugsafe user (via modal)
        async deleteDrugSafeUser() {
            try {
                this.$bvModal.hide('deleteDrugSafeUserModal');
                await deleteDrugSafeUser(this.pharmacyId, this.deleteDrugSafeUserId);
                this.$toastr.s(this.$t('drugsafe_user_deleted'));
                // remove deleted user from users
                this.drugSafeUsers = this.drugSafeUsers.filter(user => {
                    return user.id !== this.deleteDrugSafeUserId;
                });
            } catch (error) {
                handleAxiosError(error);
            }
            this.deleteDrugSafeUserId = null;
            this.submitClicked = false;
        },
        // get drugsafe users for pharmacy
        async getDrugSafeUsers() {
            try {
                const drugSafeUsers = await getDrugSafeUsers(this.pharmacyId);
                // sort users by last name and first name (case-insensitive)
                this.drugSafeUsers = _.orderBy(drugSafeUsers, [
                    user => user.last_name.toLowerCase(),
                    user => user.first_name.toLowerCase()
                ]);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // get drugsafe user roles
        async getRoles() {
            try {
                const roles = await getRoles('drugsafe');
                // only superadmin can set target user's role to "superadmin"
                roles.map(role => {
                    if (this.user.permissions.superadmin || role.slug !== 'superadmin') {
                        if (role.drugsafe_role) {
                            this.rolesDrugSafe.push(role.slug);
                        }
                    }
                });
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle "delete drugsafe user" cancel
        handleDeleteDrugSafeUserCancel() {
            this.submitClicked = false;
            this.deleteDrugSafeUserId = null;
            this.$bvModal.hide('deleteDrugSafeUserModal'); // hide confirmation modal
        },
        // handle "delete drugsafe user" click
        handleDeleteDrugSafeUserClick(drugSafeUserId) {
            this.submitClicked = true;
            this.deleteDrugSafeUserId = drugSafeUserId;
            this.$bvModal.show('deleteDrugSafeUserModal'); // show confirmation modal
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleDeleteDrugSafeUserCancel();
            }
        });
    }
};
</script>
